import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import './OpalIncidentalCallModal.css';
import TauriImage from '../TauriImage/TauriImage';
import TauriLoader2 from '../TauriLoader2/TauriLoader2';
import SearchSuggestion from './SearchSuggestion/SearchSuggestion';
import { addRecord, getAll, getNextId, getRecordById, updateRecord } from '../../localDB';
import config from '../../config.json';
import { useNavigate } from 'react-router-dom';
import appURL from '../../appURL.json';
import { accountMapping, statusMapping, sessionType, SessionTag, SyncStatusMap, activityClassification } from '../../visualizations/visualizations';
import { useBaseProvider } from '../../BaseManager/BaseManager';

const OpalIncidentalCallModal = ({ show, onHide, account, incidentalJointCall = false }) => {
    const navigate = useNavigate();

    const isCreatingRef = useRef(false);

    const [loading, setLoading] = useState(false);
    const [searchCustTerm, setSearchCustTerm] = useState("");
    const [searchInstTerm, setSearchInstTerm] = useState("");
    const [showCustSuggestion, setShowCustSuggestion] = useState(false);
    const [showInstSuggestion, setShowInstSuggestion] = useState(false);
    const [timeIn, setTimeIn] = useState(false);
    const [timeOut, setTimeOut] = useState(false);
    const [location, setLocation] = useState(null);

    const [isCustomerNew, setIsCustomerNew] = useState(false);
    const [isInstNew, setIsInstNew] = useState(false);

    const { setCoverageSearch, setIsSidebarCollapsed } = useBaseProvider();

    const [addCustomer, setAddCustomer] = useState(false);
    const [addInstitution, setAddInstitution] = useState(false);

    const [territories, setTerritories] = useState([]);

    const [errorMessage, setErrorMessage] = useState("");

    const [formState, setFormState] = useState({
        AccountID: null,
        CustomerCode: '',
        CustomerName: '',
        InstitutionID: null,
        InstitutionName: '',
        InstitutionAddress: '',
        ContactNumber: '',
        Notes: '',
        Territory: null
    });

    const getFirstMissingFieldMessage = () => {
        const { CustomerName, InstitutionName, InstitutionAddress, ContactNumber, Notes, Territory } = formState;

        if (!CustomerName?.trim()) return "Please fill in the Customer Name.";
        if (!InstitutionName?.trim()) return "Please fill in the Institution Name.";
        if (!InstitutionAddress?.trim()) return "Please fill in the Institution Address.";
        if (!ContactNumber?.trim()) return "Please fill in the Contact Number.";
        if (!Notes?.trim()) return "Please fill in the Notes.";
        if (!Territory) return "Please fill in the territory.";

        return null;
    };

    const validation = getFirstMissingFieldMessage();

    const checkAttendance = async () => {
        try {
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);

            const attendance = await getAll("Attendance");
            const todayAttendance = attendance
                .filter(a => a.SyncStatus !== SyncStatusMap.done)
                .find((a) => {
                    const timeInDate = new Date(a?.TimeIn);
                    timeInDate.setHours(0, 0, 0, 0); // normalize

                    return timeInDate.getTime() === todayDate.getTime();
                });

            setTimeIn(todayAttendance?.TimeIn ? true : false);
            setTimeOut(todayAttendance?.TimeOut ? true : false);

        } catch (error) {
            console.log("Error getting the attendance!", error);
        }
    };

    const fetchTerritories = useCallback(async () => {
        try {

            const ter = await getAll("territories");
            setTerritories(ter);

            if (ter.length === 1) {
                setFormState(prev => ({
                    ...prev,
                    Territory: ter[0]
                }));
            }

        } catch (error) {

        }
    }, []);

    useEffect(() => {
        const initialize = async () => {
            await checkAttendance();
            await fetchTerritories();
            await getLocation();
        }

        initialize();
    }, [show, fetchTerritories])

    useEffect(() => {
        if (!account) return; // skip if account is null/undefined

        const fetchAccount = async () => {
            const terr = await getRecordById("territories", account?.TerritoryID);
            const inst = await getRecordById("institution", account?.InstitutionID);

            setFormState({
                AccountID: account?.ID,
                CustomerCode: account?.Code,
                CustomerName: account?.Name,
                InstitutionID: account?.InstitutionID,
                InstitutionName: inst?.Name,
                InstitutionAddress: inst?.Address ?? location?.address,
                ContactNumber: account?.Contact_No,
                Notes: '',
                Territory: terr
            });
        };

        fetchAccount();
    }, [account]);

    const resetForm = () => {
        setLoading(false);
        setSearchCustTerm("");
        setSearchInstTerm("");
        setShowCustSuggestion(false);
        setShowInstSuggestion(false);
        setIsCustomerNew(false);
        setIsInstNew(false);
        setAddCustomer(false);
        setAddInstitution(false);
        setFormState({
            AccountID: null,
            CustomerCode: '',
            CustomerName: '',
            InstitutionID: null,
            InstitutionName: '',
            InstitutionAddress: '',
            ContactNumber: '',
            Notes: '',
            Territory: null
        });
        setErrorMessage("");
    };

    useEffect(() => {
        if (isCustomerNew) {
            setFormState((prev) => ({
                ...prev,
                AccountID: null,
                CustomerCode: '',
                InstitutionAddress: location?.address
            }));
        }
    }, [isCustomerNew]);

    useEffect(() => {
        if (isInstNew) {
            setFormState((prev) => ({
                ...prev,
                InstitutionID: null
            }));
        }
    }, [isInstNew]);

    const handleInputChange = (field) => (e) => {
        setFormState({ ...formState, [field]: e.target.value });
    };

    const handleSearchCustChange = (e) => {
        setFormState((prev) => ({
            ...prev,
            CustomerName: e.target.value
        }));
        setSearchCustTerm(e.target.value);
    };

    const handleSearchInstChange = (e) => {
        setFormState((prev) => ({
            ...prev,
            InstitutionName: e.target.value
        }));
        setSearchInstTerm(e.target.value);
    };

    const handleCustSelect = async (item) => {
        const inst = await getRecordById("institution", item?.InstitutionID);
        setFormState((prev) => ({
            ...prev,
            AccountID: item?.ID,
            CustomerCode: item?.Code,
            CustomerName: item?.Name,
            InstitutionID: item?.InstitutionID,
            InstitutionName: inst?.Name,
            InstitutionAddress: inst?.Address ?? item?.AddressDetail,
            ContactNumber: item?.Contact_No
        }));
        setAddCustomer(false);
        setShowCustSuggestion(false);
    };

    const handleInstSelect = (item) => {
        setFormState((prev) => ({
            ...prev,
            InstitutionID: item.ID,
            InstitutionName: item.Name,
            //InstitutionAddress: item.Address
        }));
        setAddInstitution(false);
        setSearchInstTerm(item.Name || '');
        setShowInstSuggestion(false);
    };

    const handleAddCustomer = () => {
        setAddCustomer(true);
        setAddInstitution(false);
        setFormState((prev) => ({
            ...prev,
            AccountID: null,
            CustomerCode: '',
            InstitutionID: null,
            InstitutionName: '',
            InstitutionAddress: location?.address,
            ContactNumber: ''
        }));
    }

    const handleAddInstitution = () => {
        setAddInstitution(true);
        setFormState((prev) => ({
            ...prev,
            InstitutionAddress: '',
        }));
    }

    const CreateCustomer = async (inst, inciID) => {
        const checkIfExist = await getRecordById("customer", formState?.AccountID);

        if (checkIfExist) {
            const newAddress = formState?.InstitutionAddress || location?.address;
            const newContact = formState?.ContactNumber;
            const updateCust = {
                ...checkIfExist,
                SyncStatus: SyncStatusMap.update
            }

            if (checkIfExist.AddressDetail !== newAddress) {
                updateCust.N_AddressDetail = newAddress;
            }

            if (checkIfExist.Contact_No !== newContact) {
                updateCust.N_Contact_No = newContact;
            }

            await updateRecord('customer', updateCust);
            return checkIfExist.ID;
        }

        const defaultAccountID = 279232; //default account Id

        const uniqueID = Number(`${defaultAccountID}${inciID ?? 0}`);

        const newCustomer = {
            "ID": uniqueID,
            "Code": "INC-ACCOUNT",
            "Name": formState?.CustomerName,
            "Specialty": null,
            "Frequency_code": null,
            "Frequency_count": null,
            "Class": null,
            "Time_call": null,
            "Day_call": null,
            "InstitutionID": inst,
            "Area": null,
            "Contact_No": formState?.ContactNumber,
            "AddressDetail": formState?.InstitutionAddress || location?.address,
            "Type": accountMapping.Default, //default
            "SyncStatus": SyncStatusMap.create
        }

        await addRecord("customer", newCustomer);
        return uniqueID;
    }

    const CreateInstitution = async () => {
        const allInst = await getAll("institution");
        const checkIfExist = allInst.find(
            (a) => a.Name.toLowerCase() === formState?.InstitutionName?.toLowerCase()
        );


        if (checkIfExist?.ID) {
            const newAddress = formState?.InstitutionAddress || location?.address;
            const updateInst = {
                ...checkIfExist,
                SyncStatus: SyncStatusMap.update
            }

            if (checkIfExist.Address !== newAddress) {
                updateInst.N_Address = newAddress;
            }

            await updateRecord("institution", updateInst);
            return checkIfExist.ID;
        }

        const nextID = await getNextId("institution");
        const newInstitution = {
            "ID": nextID,
            "Code": null,
            "Name": formState?.InstitutionName,
            "Address": formState?.InstitutionAddress || location?.address,
            "SyncStatus": SyncStatusMap.create
        }

        await addRecord("institution", newInstitution);
        return nextID;
    }

    const todayCAFormatDate = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return now.toLocaleDateString("en-CA");
    }, []);

    const CreateNotes = async (message, sessionID) => {
        const SessionNoteID = await getNextId("sessionNotes");
        const today = new Date();
        const newNoteToSave = {
            ID: SessionNoteID,
            Content: message,
            ContentType: 1,
            ReminderDate: today.toISOString().split("T")[0],
            ReminderTime: today.toISOString().split("T")[1],
            CreatedDate: today.toISOString(),
            ReminderLocation: null,
            SessionID: sessionID,
            SyncStatus: SyncStatusMap.create
        };

        await addRecord("sessionNotes", newNoteToSave);
    }

    const getLocation = async () => {
        return new Promise((resolve, reject) => {
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;

                    let locationData = { latitude, longitude };
                    if (isOnline && hasInternet) {
                        try {
                            const token = localStorage.getItem("token");
                            const response = await fetch(`${appURL.API_GetLocationAddress}?lat=${latitude}&long=${longitude}`, {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    "Authorization": `Bearer ${token}`,
                                }
                            });

                            const data = await response.json();

                            const address = data;

                            locationData.address = address;
                        } catch (err) {
                            console.error("Reverse geocoding failed:", err);
                        }
                    }

                    setLocation(locationData);
                    resolve(locationData);
                },
                (error) => {
                    console.warn("Geolocation error:", error);
                    resolve({ latitude: null, longitude: null, address: null });
                },
                {
                    enableHighAccuracy: true,
                    timeout: 20000, // stop waiting forever
                    maximumAge: 0, // 0 meaning you'll get new lat and long 
                }
            );
        });
    };

    const OnlineStartSession = async () => {
        try {
            const { latitude, longitude, address } = await getLocation();
            const cpID = localStorage.getItem("c_id");

            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            let accountID = formState?.AccountID;
            let InstitutionID = formState?.InstitutionID;

            const requestBody = {
                VersionID: 1,
                Location: {
                    lat: latitude,
                    long: longitude
                },
                Incidental: {
                    Name: formState?.CustomerName,
                    Company: formState?.InstitutionName,
                    AgentID: formState?.Territory.AgentID,
                    Address: formState?.InstitutionAddress || location?.address,
                    MobileNumber: formState?.ContactNumber,
                    Remarks: formState?.Notes,
                    TerritoryID: formState.Territory.ID,
                    AccountID: formState?.AccountID
                }
            };

            const response = await fetch(appURL.API_StartSession, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-DeviceID": devId
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (!response.ok) {
                console.log("bad request");
                return
            }

            InstitutionID = await CreateInstitution();
            // };
            // if (addCustomer) {
            accountID = await CreateCustomer(InstitutionID, data?.id);
            // };

            const nextID = await getNextId("session");
            const today = todayCAFormatDate;
            const newIncidentalCall = {
                "ID": data?.id ?? nextID,
                "SessionNumber": "",
                "Remarks": "",
                "AgentID": Number(formState?.Territory?.AgentID),
                "SessionTypeID": 1,
                "SessionDate": today,
                "CyclePeriodID": Number(cpID),
                "InstitutionID": InstitutionID,
                "AccountID": accountID,
                "RecoverySessionID": null,
                "JointCallAgentID": null,
                "JointCallRemarks": null,
                "AdvancedSessionID": null,
                "AdvancedCallReasonID": null,
                "AdvancedCallRemarks": null,
                "MissedCallReasonID": null,
                "MissedCallRemarks": null,
                "PastDueSessionID": null,
                "ActualStart": data?.server_date ?? new Date().toISOString(),
                "ActualEnd": null,
                "ActualLocationLat": latitude,
                "ActualLocationLong": longitude,
                "ActualLocation": address ?? location?.address,
                "RecipientSignatureFileID": null,
                "RecipientPhotoFileID": null,
                "Duration": null,
                "Status": statusMapping.inprogress,
                "VersionID": data?.version ?? 1,
                "LastUpdatedDate": null,
                "Type": sessionType.Incidental,
                "Joint": false,
                "Activity": {
                    "Classification": activityClassification.System,
                    "Description": "Regular"
                }
            }

            await addRecord("session", newIncidentalCall);

            // await CreateNotesOnline(formState?.Notes, data?.id);
            // const date = result?.serverDate ?? new Date().toISOString();
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(d));

            //to prevent the initial load to select the first item in sidebar 
            setCoverageSearch('');
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${data?.id}`);
            setIsSidebarCollapsed(true)
            resetForm();
            onHide();

        } catch (error) {
            console.error("Error starting session:", error);
        }
    };

    const OfflineStartSession = async () => {
        try {
            const { latitude, longitude, address } = await getLocation();
            const cpID = localStorage.getItem("c_id");

            let accountID = formState?.AccountID;
            let InstitutionID = formState?.InstitutionID;

            const nextID = await getNextId("session");
            // if (addInstitution) {
            InstitutionID = await CreateInstitution();
            // };
            // if (addCustomer) {
            accountID = await CreateCustomer(InstitutionID, nextID);
            // };

            const today = todayCAFormatDate;
            const newIncidentalCall = {
                "ID": nextID,
                "Remarks": "",
                "AgentID": Number(formState?.Territory?.AgentID),
                "SessionTypeID": 1,
                "SessionDate": today,
                "CyclePeriodID": Number(cpID),
                "InstitutionID": InstitutionID,
                "AccountID": accountID,
                "RecoverySessionID": null,
                "JointCallAgentID": null,
                "JointCallRemarks": null,
                "AdvancedSessionID": null,
                "AdvancedCallReasonID": null,
                "AdvancedCallRemarks": null,
                "MissedCallReasonID": null,
                "MissedCallRemarks": null,
                "PastDueSessionID": null,
                "ActualStart": new Date().toISOString(),
                "ActualEnd": null,
                "ActualLocationLat": latitude,
                "ActualLocationLong": longitude,
                "ActualLocation": address ?? location?.address,
                "RecipientSignatureFileID": null,
                "RecipientPhotoFileID": null,
                "Duration": null,
                "Status": statusMapping.inprogress,
                "VersionID": 1,
                "LastUpdatedDate": null,
                "Type": sessionType.Incidental,
                "Joint": false,
                "Activity": {
                    "Classification": activityClassification.System,
                    "Description": "Regular"
                },
                "SyncStatus": SyncStatusMap.create
            }

            await addRecord("session", newIncidentalCall);

            await CreateNotes(formState?.Notes, nextID);
            // const date = result?.serverDate ?? new Date().toISOString();
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(d));

            //to prevent the initial load to select the first item in sidebar 
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${nextID}`);
            setIsSidebarCollapsed(true);
            resetForm();
            onHide();

        } catch (error) {
            console.error("Error starting session:", error);
        }
    };

    const CreatingIncidental = useCallback(async () => {
        if (isCreatingRef.current) return; // prevent multiple calls
        isCreatingRef.current = true;

        if (validation) {
            setErrorMessage(validation);
            isCreatingRef.current = false;
            return
        }
        setLoading(true);
        try {
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                await OnlineStartSession();
            } else {
                await OfflineStartSession();
            }

        } catch (error) {
            console.log(error);
        } finally {
            isCreatingRef.current = false;
            setLoading(false);
        }
    }, [formState, validation, CreateInstitution, CreateCustomer, CreateNotes, OnlineStartSession]);

    return (
        <Modal className="opal-incidental-modal" show={show} onHide={onHide} centered>
            <Modal.Header className="incidental-header">
                INCIDENTAL CALL
            </Modal.Header>
            <Modal.Body className="incidental-body">
                <Form>
                    <Form.Group>
                        <Form.Label>NAME</Form.Label>
                        <div className="name-input">
                            <Form.Control
                                type="text"
                                value={formState.CustomerName}
                                onMouseDown={() => setShowCustSuggestion(true)}
                                onChange={handleSearchCustChange}
                            />
                            {isCustomerNew && (
                                <TauriImage
                                    localImage="/Images/new.svg"
                                    className="input-icon"
                                    alt="icon"
                                    color="#049D33"
                                />
                            )}
                            <SearchSuggestion
                                show={showCustSuggestion}
                                setShowSuggestion={setShowCustSuggestion}
                                search={searchCustTerm}
                                onSelect={handleCustSelect}
                                type="cust"
                                setIsNew={setIsCustomerNew}
                                onAddNew={handleAddCustomer}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>INSTITUTION</Form.Label>
                        <div className="institution-input">
                            <Form.Control
                                type="text"
                                value={formState.InstitutionName}
                                onMouseDown={() => setShowInstSuggestion(true)}
                                onChange={handleSearchInstChange}
                            />
                            {isInstNew && (
                                <TauriImage
                                    localImage="/Images/new.svg"
                                    className="input-icon"
                                    alt="icon"
                                    color="#049D33"
                                />
                            )}
                            <SearchSuggestion
                                show={showInstSuggestion}
                                setShowSuggestion={setShowInstSuggestion}
                                search={searchInstTerm}
                                onSelect={handleInstSelect}
                                type="inst"
                                setIsNew={setIsInstNew}
                                onAddNew={handleAddInstitution}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>INSTITUTION ADDRESS</Form.Label>
                        <div className='institution-address-input'>
                            <Form.Control
                                type="text"
                                value={formState.InstitutionAddress}
                                onChange={handleInputChange('InstitutionAddress')}
                            />
                            <TauriImage
                                localImage="/Images/line-md--map-marker-plus-filled.svg"
                                className="input-icon"
                                alt="icon"
                                color="#049D33"
                                onClick={() => {
                                    if (location?.address !== null && location?.address !== undefined && location?.address !== "") {
                                        setFormState(prev => ({
                                            ...prev,
                                            InstitutionAddress: location.address
                                        }));
                                    }
                                }}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>CONTACT NUMBER</Form.Label>
                        <Form.Control
                            type="text"
                            value={formState.ContactNumber}
                            onChange={handleInputChange('ContactNumber')}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>NOTES</Form.Label>
                        <Form.Control
                            type="text"
                            value={formState.Notes}
                            onChange={handleInputChange('Notes')}
                        />
                    </Form.Group>
                    {territories.length > 1 && (
                        <Form.Group>
                            <Form.Label>TERRITORY</Form.Label>
                            <Dropdown>
                                <Dropdown.Toggle className="form-control text-start custom-dropdown-toggle">
                                    {formState.Territory?.Name || "-- Select Territory --"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="w-100">
                                    {territories.map(i => (
                                        <Dropdown.Item
                                            key={i.ID}
                                            onClick={() =>
                                                setFormState(prev => ({
                                                    ...prev,
                                                    Territory: i
                                                }))
                                            }
                                        >
                                            <span>{i.Name}</span>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Form.Group>
                    )}
                    {errorMessage && (
                        <Form.Text className="text-danger d-flex align-items-center mt-2">
                            {errorMessage}
                        </Form.Text>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className="incidental-footer position-relative">
                <div className="position-relative">
                    <Button className="btn-finish-session d-flex" disabled={loading || (!timeIn || timeOut)} onClick={CreatingIncidental}>
                        {loading && (
                            <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.2rem" color="#FFF" />
                        )}
                        <span className="text-nowrap">Start Session</span>
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default OpalIncidentalCallModal;
